import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { O2_RANGE } from '../../../_constants';

const Root = styled.div`
  width: 100%;
  padding: 10px 0 0;

  .gauge-container-bar-cont {
    position: relative;
    height: 10px;
    display: flex;
    align-items: center;

    .gauge-container-bar {
      display: flex;
      width: 100%;
      height: 2px;
      gap: 1px;
    }
  }

  .gauge-container-bar-text {
    display: flex;
    margin-top: 2px;

    .gauge-bar-label {
      display: -webkit-box;
      -webkit-line-clamp: 1;
      white-space: nowrap;
      text-overflow: ellipsis;
      overflow: hidden;
      line-height: 1.2;
    }
  }
`;

interface SensorLiveGaugeProps {
  sensorType: string;
  value: number | null;
  ranges: any;
}

// 상태 컬러 매핑 (CSS 변수 활용)
const getStatusColor = (status: string) => {
  switch (status) {
    case '관심':
      return 'var(--filled-green)';
    case '주의 2':
      return 'var(--filled-amber)';
    case '경고 2':
      return 'var(--filled-orange)';
    case '좋음':
    case '저온':
    case '건조':
    case '적정 2':
      return 'var(--primary)';
    case '주의':
    case '적정':
      return 'var(--filled-green)';
    case '경고':
      return 'var(--filled-amber)';
    case '위험':
    case '고온':
    case '다습':
      return 'var(--filled-red)';
    default:
      return 'var(--text-tertiary)';
  }
};

const SensorLiveGauge = ({ sensorType, value, ranges }: SensorLiveGaugeProps) => {
  const { t } = useTranslation();
  const sData = ranges?.[sensorType];

  // 센서별 구간 라벨 정의
  const getDisplayRanges = () => {
    if (sensorType === 'TEMP') return [{ label: '저온' }, { label: '적정' }, { label: '고온' }];
    if (sensorType === 'HUMI') return [{ label: '건조' }, { label: '적정' }, { label: '다습' }];
    if (sensorType === 'FEELS') return [{ label: '적정 2' }, { label: '관심' }, { label: '주의 2' }, { label: '경고 2' }, { label: '위험' }]; // 라벨명 뒤 숫자 2는 해당 라벨명에 컬러를 다르게 하기 위함
    return [{ label: '좋음' }, { label: '주의' }, { label: '경고' }, { label: '위험' }];
  };
  const displayRanges = getDisplayRanges();

  // 범위 수치 텍스트
  const getRangeText = (idx: number) => {
    if (sensorType === 'O2') return ['19~22.5', '19↓/22.5↑', '18.5↓/23↑', '18↓/23.5↑'][idx] || '';
    if (sensorType === 'TEMP') return ['18°↓', '18°↑', '27°↑'][idx] || '';
    if (sensorType === 'HUMI') return ['30↓', '30↑', '60↑'][idx] || '';
    if (sensorType === 'VOC') return ['0', '1', '2', '3'][idx] || '';
    if (sensorType === 'FEELS') return ['31°↓', '31°↑', '33°↑', '35°↑', '38°↑'][idx] || '';
    if (sData) {
      const { min, level1, level2, level3, max } = sData;
      const setRangeText = [`${level1 ?? ''}↓`, `${level1 ?? ''}↑`, `${level2 ?? ''}↑`, `${level3 ?? ''}↑`];
      return setRangeText[idx] || '';
    }
    return '';
  };

  // 구간별 균등 배분 위치 계산 로직
  const getPointerPosition = (): { pointerPos: number; statusIndex: number } => {
    if (value === null || value === undefined) return { pointerPos: 0, statusIndex: 0 };

    // 산소 경우 범위 계산
    if (sensorType === 'O2') {
      const matchedRange = [...O2_RANGE].reverse().find((o2Range) => {
        const [lowerMin, lowerMax] = o2Range.lower;
        const [upperMin, upperMax] = o2Range.upper;

        // 하한선(lower) 체크: -1은 무시, 값이 범위 내에 있는지 확인
        const isLowerBreached = (lowerMin !== -1 && value < lowerMin) || (lowerMax !== -1 && value <= lowerMax);

        // 상한선(upper) 체크: -1은 무시
        const isUpperBreached = (upperMin !== -1 && value > upperMin) || (upperMax !== -1 && value >= upperMax);

        return isLowerBreached || isUpperBreached;
      });

      const statusIdx = matchedRange ? Number(matchedRange.level) : 0;

      // 4칸 기준 한 칸의 너비는 25%
      const segmentWeight = 100 / 4;
      // 무조건 해당 칸의 중앙(0.5)으로 고정
      const fixedPointerPos = statusIdx * segmentWeight + segmentWeight * 0.5;

      return { pointerPos: fixedPointerPos, statusIndex: statusIdx };
    }

    if (sensorType === 'VOC') {
      const segmentWeight = 100 / 4;
      const fixedPointerPos = value * segmentWeight + segmentWeight * 0.5;

      return { pointerPos: fixedPointerPos, statusIndex: value };
    }

    // 계산용 임계치 배열 구성 [Min, L1, L2, (L3), Max]
    let thresholds: number[] = [];
    if (sensorType === 'TEMP') thresholds = [-20, 18, 27, 65];
    else if (sensorType === 'HUMI') thresholds = [0, 30, 60, 100];
    else if (sensorType === 'VOC') thresholds = [0, 1, 2, 3];
    else if (sensorType === 'FEELS') thresholds = [0, 31, 33, 35, 38, 100];
    else if (sData && (sData.level1 || sData.level2 || sData.level3)) thresholds = [sData.min, sData.level1, sData.level2, sData.level3, sData.max];
    else return { pointerPos: 0, statusIndex: 0 };

    const numSegments = thresholds.length - 1;
    const segmentWeight = 100 / numSegments; // 한 칸당 차지하는 전체 대비 %

    // 현재 값이 어느 칸에 속하는지 인덱스 찾기
    let idx = 0;
    if (sensorType === 'O2') {
      // 산소 전용 인덱스 찾기 (내림차순 thresholds 대응)
      idx = thresholds.findIndex((threshold, i) => i > 0 && value >= threshold) - 1;
      if (idx === -2 || value <= thresholds[thresholds.length - 1]) idx = numSegments - 1;
      if (idx === -1) idx = 0;
    } else {
      // 일반 센서 (오름차순 thresholds 대응)
      idx = thresholds.findIndex((threshold, i) => i > 0 && value < threshold) - 1;
      if (idx === -2 || value >= thresholds[thresholds.length - 1]) idx = numSegments - 1;
      if (idx === -1) idx = 0;
    }

    // 일반 센서는 수치에 따라 정밀 계산
    const segMin = thresholds[idx];
    const segMax = thresholds[idx + 1];
    const diff = segMax - segMin;
    const progress = diff === 0 ? 0 : (value - segMin) / diff;

    // 최종 위치 = (이미 지나온 칸들의 너비) + (현재 칸 내에서의 이동분)
    return { pointerPos: idx * segmentWeight + Math.min(Math.max(progress, 0), 1) * segmentWeight, statusIndex: idx };
  };

  const { pointerPos, statusIndex } = getPointerPosition();

  return (
    <Root className={`gauge-container-${sensorType.toLowerCase()}`}>
      {/* 게이지 바 영역 */}
      <div className='gauge-container-bar-cont'>
        <div className='gauge-container-bar'>
          {displayRanges.map((range) => (
            <div
              key={range.label}
              style={{
                flex: 1,
                backgroundColor: getStatusColor(range.label),
              }}
            />
          ))}
        </div>

        {/* 현재 값 - 동그라미 포인터 */}
        {((value !== null && (sensorType === 'TEMP' || sensorType === 'HUMI' || sensorType === 'O2' || sensorType === 'VOC')) ||
          (value !== null && Object.keys(ranges).length > 0 && sData && (sData.level1 || sData.level2 || sData.level3))) && (
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: `${pointerPos}%`,
              width: '10px',
              height: '10px',
              backgroundColor: 'var(--tonal)',
              borderRadius: '50%',
              border: `1px solid ${getStatusColor(displayRanges[statusIndex].label)}`,
              boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
              transform: 'translate(-50%, -50%)',
              transition: 'left 0.5s cubic-bezier(0.4, 0, 0.2, 1), border-color 0.3s',
              zIndex: 10,
            }}
          />
        )}
      </div>

      {/* 하단 상태 텍스트 영역 */}
      <div className='gauge-container-bar-text'>
        {displayRanges.map((range, idx) => {
          const color = getStatusColor(range.label);
          return (
            <div
              key={range.label}
              style={{
                display: 'flex',
                justifyContent: 'center',
                flex: 1,
                textAlign: 'center',
                fontSize: '12px',
                color,
                fontWeight: '600',
                alignItems: 'center',
              }}
            >
              <span className='gauge-bar-label'>
                {t(range.label.split(' ')[0])}
                {((Object.keys(ranges).length === 0 && (sensorType === 'TEMP' || sensorType === 'HUMI' || sensorType === 'O2' || sensorType === 'VOC')) || Object.keys(ranges).length > 0) && (
                  <>({getRangeText(idx)})</>
                )}
              </span>
            </div>
          );
        })}
      </div>
    </Root>
  );
};

export default SensorLiveGauge;
